import { ReactRenderer } from '@tiptap/react';
import type { SuggestionOptions } from '@tiptap/suggestion';
import Fuse from 'fuse.js';
import type { Instance as TippyInstance } from 'tippy.js';
import tippy from 'tippy.js';

import type { ContextFieldCategory } from '@/services/api/templatesV2ServiceEnhanced';

import { MergeFieldPicker } from './MergeFieldPicker';

const LIMIT = 15;

export type MergeFieldItem = {
  key: string;
  label: string;
  category: string;
  sample: string;
};

export function createMergeFieldSuggestion(
  categories: ContextFieldCategory[],
): Partial<SuggestionOptions<MergeFieldItem>> {
  const allItems: MergeFieldItem[] = categories.flatMap((cat) =>
    cat.fields.map((f) => ({
      key: f.key,
      label: f.label,
      category: cat.label,
      sample: f.sample,
    })),
  );

  const fuse = new Fuse(allItems, {
    keys: ['key', 'label', 'category'],
    threshold: 0.35,
    includeScore: true,
  });

  return {
    char: '{',
    items: ({ query }) => {
      if (!query) return allItems.slice(0, LIMIT);
      return fuse
        .search(query)
        .slice(0, LIMIT)
        .map(({ item }) => item);
    },

    render: () => {
      let component: ReactRenderer<any>;
      let popup: TippyInstance[];

      return {
        onStart: (props) => {
          component = new ReactRenderer(MergeFieldPicker, {
            props,
            editor: props.editor,
          });

          popup = tippy('body', {
            getReferenceClientRect: props.clientRect as any,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
          });
        },

        onUpdate: (props) => {
          component.updateProps(props);
          popup[0].setProps({
            getReferenceClientRect: props.clientRect as any,
          });
        },

        onKeyDown: (props) => {
          if (props.event.key === 'Escape') {
            popup[0].hide();
            return true;
          }
          return (component.ref as any)?.onKeyDown(props);
        },

        onExit: () => {
          popup[0].destroy();
          component.destroy();
        },
      };
    },

    command: ({ editor, range, props }) => {
      const {nodeAfter} = editor.view.state.selection.$to;
      const overrideSpace = nodeAfter?.text?.startsWith(' ');

      if (overrideSpace) {
        range.to += 1;
      }

      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          { type: 'mergeField', attrs: { key: props.key, label: props.label, sample: props.sample } },
          { type: 'text', text: ' ' },
        ])
        .run();

      editor.view.dom.ownerDocument.defaultView
        ?.getSelection()
        ?.collapseToEnd();
    },
  };
}
